/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getExportInvoiceHistory from "@stoneage/App/Handlers/InvoiceHistory/getExportInvoiceHistoryHandler";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import React, { useContext } from "react";
import { connect, ConnectedProps } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
    };
};

const mapDispatchToProps = {
    getExportInvoiceHistory,
};

const enum fields {
    buttonText = "buttonText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.buttonText]: string;
    };
}

type Props = OwnProps & PropFromRedux;

export interface ExportInvoiceHistoryButtonStyles {
    exportButton: LinkPresentationProps;
}

export const exportInvoiceHistoryButtonStyles: ExportInvoiceHistoryButtonStyles = {
    exportButton: {
        color: "common.black",
        typographyProps: {
            size: "14px",
            lineHeight: "18px",
            color: "common.black",
        },
        icon: {
            iconProps: {
                src: "Share",
                size: 14,
                css: css`
                    margin-right: 8px;
                `,
            },
        },
        css: css`
            height: 27px;
        `,
    },
};

const styles = exportInvoiceHistoryButtonStyles;

const ExportInvoiceHistoryButton = ({
    id,
    fields: { buttonText },
    isAuthenticated,
    getExportInvoiceHistory,
}: Props) => {
    if (!isAuthenticated) {
        return <></>;
    }
    const toasterContext = useContext(ToasterContext);

    const clickHandler = () => {
        getExportInvoiceHistory({
            onError: error => {
                toasterContext.addToast({ body: error, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    toasterContext.addToast({
                        body: translate("File has been successfully exported"),
                        messageType: "success",
                    });
                } else if (resultProps.apiResult?.errorMessage) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onError?.(resultProps.apiResult.errorMessage);
                    toasterContext.addToast({ body: resultProps.apiResult.errorMessage, messageType: "danger" });
                }
            },
        });
    };

    return (
        <Link {...styles.exportButton} onClick={clickHandler}>
            {buttonText}
        </Link>
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropFromRedux = ConnectedProps<typeof connector>;

const widgetModule: WidgetModule = {
    component: connector(ExportInvoiceHistoryButton),
    definition: {
        group: "Invoice History",
        allowedContexts: ["InvoiceHistoryPage"],
        fieldDefinitions: [
            {
                name: fields.buttonText,
                displayName: "Export Invoice History Button Text",
                editorTemplate: "TextField",
                defaultValue: "Export",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
